import { useState } from "react";
import { toast } from "sonner";
import { Loader2, School, Trash2, FileBarChart, Send, KeyRound, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, fmtINR } from "@/lib/api";

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join(", ") : d || "Failed";
};

const prevMonth = () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 7); };

export const AnnounceChampionButton = ({ auth }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const month = window.prompt("Announce Maker of the Month for which month? (YYYY-MM)", prevMonth());
    if (!month) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/champions/announce", null, { ...auth, params: { month } });
      toast.success(`Maker of the Month ${data.month}: ${data.student_name} (${data.projects} projects) — ${data.already_announced ? "already announced earlier (no new email)" : "parent emailed"}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={run} disabled={busy} data-testid="announce-champion-btn">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trophy className="w-4 h-4 mr-1" />Maker of the Month</>}
    </Button>
  );
};

export const AddSchoolDialog = ({ auth, onDone }) => {
  const empty = { school_name: "", principal_name: "", email: "" };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/admin/schools", f, auth); toast.success("School added"); setF(empty); setOpen(false); onDone(); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-white border border-[#00E5FF]/40 hover:bg-[#FF3B3B] hover:border-[#FF3B3B] text-[#0A192F] hover:text-white" data-testid="add-school-btn"><School className="w-4 h-4 mr-1" />Add school</Button></DialogTrigger>
      <DialogContent className="bg-white" aria-describedby={undefined} data-testid="add-school-dialog">
        <DialogHeader><DialogTitle>Add school for monthly reports</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Input required placeholder="School name (must match enrollment school)" value={f.school_name} onChange={(e) => setF({ ...f, school_name: e.target.value })} data-testid="school-name-input" />
          <Input required placeholder="Principal name" value={f.principal_name} onChange={(e) => setF({ ...f, principal_name: e.target.value })} data-testid="principal-name-input" />
          <Input required type="email" placeholder="Principal email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="principal-email-input" />
          <p className="text-xs text-slate-500">Reports are emailed automatically on the 1st of every month (9 AM IST) for the previous month.</p>
          <Button type="submit" disabled={loading} className="w-full rs-cta rounded-full" data-testid="school-create-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save school"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Totals = ({ t, count }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center" data-testid="report-totals">
    {[["Students", count], ["Attendance", `${t.attendance_pct}%`], ["Projects", t.projects], ["Earnings", fmtINR(t.earnings_inr)], ["Certificates", t.certificates]].map(([k, v]) => (
      <div key={k} className="bg-[#E8F0FE] rounded-lg p-2"><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div className="font-extrabold text-[#0A192F]">{v}</div></div>
    ))}
  </div>
);

export const PrincipalLoginButton = ({ school, auth, onDone }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/schools/${school.id}/login`, { password: pw }, auth);
      toast.success(`Principal login ready: ${data.email}`);
      setPw(""); setOpen(false); onDone();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="h-7" data-testid={`principal-login-btn-${school.id}`}><KeyRound className="w-3.5 h-3.5 mr-1" />{school.has_login ? "Reset login" : "Create login"}</Button></DialogTrigger>
      <DialogContent className="bg-white" aria-describedby={undefined} data-testid="principal-login-dialog">
        <DialogHeader><DialogTitle>Principal login — {school.principal_name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <p className="text-sm text-slate-600">Login email: <b>{school.email}</b>. They sign in at <b>/principal/login</b> to see a live dashboard of {school.school_name}.</p>
          <Input required minLength={8} placeholder="Set password (min 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="principal-password-input" />
          <Button type="submit" disabled={loading} className="w-full rs-cta rounded-full" data-testid="principal-login-save-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save login"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const SchoolActions = ({ school, auth, onDone }) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(prevMonth());
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState("");

  const preview = async (m = month) => {
    setBusy("load");
    try { const { data } = await api.get(`/admin/schools/${school.id}/report`, { ...auth, params: { month: m } }); setRep(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const send = async () => {
    setBusy("send");
    try { const { data } = await api.post(`/admin/schools/${school.id}/send-report`, null, { ...auth, params: { month } }); toast.success(`Report for ${data.month} emailed to ${data.sent_to}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${school.school_name} from monthly reports?`)) return;
    try { await api.delete(`/admin/schools/${school.id}`, auth); toast.success("School removed"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex gap-2">
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) preview(); }}>
        <DialogTrigger asChild><Button size="sm" variant="outline" className="h-7" data-testid={`school-report-btn-${school.id}`}><FileBarChart className="w-3.5 h-3.5 mr-1" />Report</Button></DialogTrigger>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="school-report-dialog">
          <DialogHeader><DialogTitle>{school.school_name} — monthly report</DialogTitle></DialogHeader>
          <div className="flex gap-2 items-center flex-wrap">
            <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); preview(e.target.value); }} className="h-9 w-44" data-testid="report-month-input" />
            <Button onClick={send} disabled={!!busy} className="rs-cta rounded-full h-9" data-testid="report-send-btn">
              {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Email to {school.email}</>}
            </Button>
          </div>
          {busy === "load" && <Loader2 className="w-5 h-5 animate-spin mx-auto my-6" />}
          {rep && busy !== "load" && (
            <div className="space-y-4">
              <Totals t={rep.totals} count={rep.student_count} />
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-white text-left"><tr>{["Student", "Class", "Program", "Present", "Projects", "Earnings", "Certified"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                <tbody>
                  {rep.students.map((s, i) => (
                    <tr key={i} className="border-t"><td className="px-2 py-1.5 font-semibold">{s.student_name}</td><td className="px-2">{s.class_grade}</td><td className="px-2">{s.program}</td>
                      <td className="px-2">{s.present}/{s.sessions}</td><td className="px-2">{s.projects_completed}</td><td className="px-2">{fmtINR(s.earnings_inr)}</td><td className="px-2">{s.certified ? "Yes" : "—"}</td></tr>
                  ))}
                  {rep.students.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-slate-500">No paid students matched this school name.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <PrincipalLoginButton school={school} auth={auth} onDone={onDone} />
      <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={del} data-testid={`delete-school-${school.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
    </div>
  );
};
